/**
 * Global Property Constants
 * 
 * Constant arrays and values used across global property components.
 */

// Property types available for global properties
export const PROPERTY_TYPES: Array<'string' | 'number' | 'integer' | 'float' | 'boolean' | 'date' | 'date_range' | 'currency' | 'duration' | 'url' | 'uuid' | 'uuid[]' | 'list'> = [
  'string',
  'number',
  'integer',
  'float',
  'boolean',
  'date',
  'date_range',
  'currency',
  'duration',
  'url',
  'uuid',
  'uuid[]',
  'list'
];

// Calculation types for property dependencies
export const CALCULATION_TYPES = [
  { value: 'add', label: 'Add' },
  { value: 'subtract', label: 'Subtract' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'divide', label: 'Divide' },
  { value: 'sum', label: 'Sum' },
  { value: 'avg', label: 'Average' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'function', label: 'Function' },
  { value: 'custom', label: 'Custom' }
];

// Function types for system-generated properties
export const FUNCTION_TYPES = [
  { value: 'Assessment', label: 'Assessment' },
  { value: 'improvement_version', label: 'Improvement Version' },
  { value: 'get_krysalis_user', label: 'Get Krysalis User' },
  { value: 'transaction_status', label: 'Transaction Status' },
  { value: 'generate_date_range', label: 'Generate Date Range', propertyTypes: ['date_range'] },
  { value: 'generate_improvement_effective_date_range', label: 'Generate Improvement Effective Date Range', propertyTypes: ['date_range'] },
  { value: 'transition_date', label: 'Transition Date', propertyTypes: ['date'] }
];

// Date range interval types for generate_date_range function
export const DATE_RANGE_INTERVALS = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'semi_annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' }
];

// Input types for get_krysalis_user function
export const KRYSA_USER_INPUT_TYPES = [
  { value: 'current_user', label: 'Current User' },
  { value: 'select_user', label: 'Select User' },
  { value: 'referenced_user', label: 'Referenced User' }
];

// Output fields for get_krysalis_user function
export const KRYSA_USER_OUTPUT_FIELDS = [
  { value: 'name', label: 'Name (Full Name)' },
  { value: 'username', label: 'Username' },
  { value: 'email', label: 'Email' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'is_active', label: 'Is Active' },
  { value: 'is_superuser', label: 'Is Superuser' }
];

// Source types for property references
export const SOURCE_TYPES = [
  { value: 'same_artifact', label: 'Same Artifact' },
  { value: 'parent_artifact', label: 'Parent Artifact' },
  { value: 'child_artifact', label: 'Child Artifact' },
  { value: 'related_artifact', label: 'Related Artifact' }
];

// Input mask types for string properties
export const INPUT_MASK_TYPES = [
  { value: 'phone', label: 'Phone Number', pattern: '^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$' },
  { value: 'phone_us', label: 'US Phone (XXX) XXX-XXXX', pattern: '^\\(?([0-9]{3})\\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$' },
  { value: 'phone_international', label: 'International Phone', pattern: '^\\+?[1-9]\\d{1,14}$' },
  { value: 'email', label: 'Email Address', pattern: '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$' },
  { value: 'address_street', label: 'Street Address', pattern: '^\\d+\\s+[a-zA-Z0-9\\s,\\.]+$' },
  { value: 'address_city', label: 'City', pattern: '^[a-zA-Z\\s]+$' },
  { value: 'address_state', label: 'State/Province', pattern: '^[a-zA-Z\\s]+$' },
  { value: 'address_zip_us', label: 'US ZIP Code (XXXXX or XXXXX-XXXX)', pattern: '^\\d{5}(-\\d{4})?$' },
  { value: 'address_postal_ca', label: 'Canadian Postal Code', pattern: '^[A-Za-z]\\d[A-Za-z][ -]?\\d[A-Za-z]\\d$' },
  { value: 'ssn', label: 'SSN (XXX-XX-XXXX)', pattern: '^\\d{3}-\\d{2}-\\d{4}$' },
  { value: 'credit_card', label: 'Credit Card (XXXX-XXXX-XXXX-XXXX)', pattern: '^\\d{4}-\\d{4}-\\d{4}-\\d{4}$' },
  { value: 'date_mmddyyyy', label: 'Date (MM/DD/YYYY)', pattern: '^(0[1-9]|1[0-2])\\/(0[1-9]|[12]\\d|3[01])\\/\\d{4}$' },
  { value: 'time_hhmm', label: 'Time (HH:MM)', pattern: '^([01]\\d|2[0-3]):([0-5]\\d)$' },
  { value: 'custom', label: 'Custom Mask' }
];
